import { type FC } from "react";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import type { VirtualClassFormValues } from "./types";

import { t } from "i18n/i18n";
import { Checkbox } from "mds/components/Checkbox";
import { FormField } from "mds/components/FormField";
import { Listbox } from "mds/components/Listbox";
import { Tooltip } from "mds/components/Tooltip";
import { selectDesignatedSection } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { getAvServiceFormItems } from "utils/av";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isLinkedSection } from "utils/publishing";

interface VirtualClassFormProps {
  className?: string;
  form: UseFormReturn<VirtualClassFormValues>;
}

export const VirtualClassForm: FC<VirtualClassFormProps> = ({ className, form }) => {
  const designatedSection = useObjectSelector(selectDesignatedSection, [
    "archived_at",
    "kind",
    "template_id",
    "term_id",
  ]);
  const { isArchived } = useIsArchived();

  if (!designatedSection) {
    return null;
  }

  const disabled = isLinkedSection(designatedSection) || isArchived(designatedSection);

  const avEnabled = form.watch("av_enabled");
  const avService = form.watch("av_service");

  return (
    <FormProvider {...form}>
      <div className={twMerge("flex flex-col", className)}>
        <div className="flex flex-col gap-3">
          <Checkbox
            {...form.register("av_enabled", { disabled })}
            eventName="Section Settings - Virtual Classes toggle"
          >
            {t("settings.section_settings_av_enabled.label")}
          </Checkbox>

          <Tooltip content={t("settings.section_settings_auto_mute.tooltip")}>
            <Checkbox
              {...form.register("mute_students_on_main_room_entry", {
                disabled: !avEnabled || disabled,
              })}
              eventName="Section Settings - Mute students on main room entry toggle"
            >
              {t("settings.section_settings_auto_mute.label")}
            </Checkbox>
          </Tooltip>

          <Checkbox
            {...form.register("auto_recordings_enabled", {
              disabled: !avEnabled || disabled,
            })}
            eventName="Section Settings - Auto record class toggle"
          >
            {t("settings.section_settings_auto_recordings.label")}
          </Checkbox>

          <FormField
            disabled={!avEnabled || disabled}
            label={t("settings.section_settings_av_service.label")}
            name="av_service"
            required
          >
            {(controlProps) => (
              <Listbox
                {...controlProps}
                eventName="Section Settings - AV service selection"
                eventPropertyKey="newAvService"
                items={getAvServiceFormItems()}
                selectedValue={avService}
                onSelectionChange={({ id }) =>
                  form.setValue("av_service", id, { shouldDirty: true })
                }
              />
            )}
          </FormField>
        </div>
      </div>
    </FormProvider>
  );
};
